/** Semaine calendaire Europe/Paris — aligné L1b / parisDateTime backend. */

export const APP_TIMEZONE = 'Europe/Paris';

export const WEEK_DAY_COLUMNS = [
  { value: 1, short: 'Lun' },
  { value: 2, short: 'Mar' },
  { value: 3, short: 'Mer' },
  { value: 4, short: 'Jeu' },
  { value: 5, short: 'Ven' },
  { value: 6, short: 'Sam' },
  { value: 0, short: 'Dim' },
];

function parisYmd(date = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: APP_TIMEZONE }).format(date);
}

function parisDayOfWeek(ymd) {
  const noon = new Date(`${ymd}T12:00:00`);
  const short = noon.toLocaleDateString('en-US', { timeZone: APP_TIMEZONE, weekday: 'short' });
  const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return map[short] ?? 0;
}

function addDaysYmd(ymd, days) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days, 12, 0, 0));
  return dt.toISOString().slice(0, 10);
}

/** Lundi de la semaine calendaire Paris contenant la date de référence. */
export function getParisWeekMondayYmd(weekOffset = 0, reference = new Date()) {
  const today = parisYmd(reference);
  const dow = parisDayOfWeek(today);
  const diffToMonday = dow === 0 ? -6 : 1 - dow;
  let monday = addDaysYmd(today, diffToMonday);
  if (weekOffset) {
    monday = addDaysYmd(monday, weekOffset * 7);
  }
  return monday;
}

export function getParisWeekDays(weekOffset = 0, reference = new Date()) {
  const monday = getParisWeekMondayYmd(weekOffset, reference);
  return Array.from({ length: 7 }, (_, i) => {
    const ymd = addDaysYmd(monday, i);
    return {
      ymd,
      jourSemaine: parisDayOfWeek(ymd),
      date: new Date(`${ymd}T12:00:00`),
    };
  });
}

export function getParisWeekRangeLabel(weekOffset = 0, reference = new Date()) {
  const days = getParisWeekDays(weekOffset, reference);
  const start = days[0].date;
  const end = days[6].date;
  const fmt = new Intl.DateTimeFormat('fr-FR', {
    timeZone: APP_TIMEZONE,
    day: 'numeric',
    month: 'long',
  });
  const year = new Intl.DateTimeFormat('fr-FR', {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
  }).format(end);
  return `${fmt.format(start)} — ${fmt.format(end)} ${year}`;
}

/** Bornes ISO UTC pour requête API dateMin/dateMax (journée Paris). */
export function getParisWeekApiRange(weekOffset = 0, reference = new Date()) {
  const days = getParisWeekDays(weekOffset, reference);
  const dateMin = `${days[0].ymd}T00:00:00`;
  const dateMax = `${days[6].ymd}T23:59:59`;
  return { dateMin, dateMax, days };
}

/** Mois calendaire Paris — monthOffset 0 = mois courant. */
export function getParisMonthMeta(monthOffset = 0, reference = new Date()) {
  const todayYmd = parisYmd(reference);
  const [y0, m0] = todayYmd.split('-').map(Number);
  let monthIndex = m0 - 1 + monthOffset;
  let year = y0;
  while (monthIndex < 0) {
    monthIndex += 12;
    year -= 1;
  }
  while (monthIndex > 11) {
    monthIndex -= 12;
    year += 1;
  }
  const month = monthIndex + 1;
  const firstYmd = `${year}-${String(month).padStart(2, '0')}-01`;
  const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const days = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const ymd = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    days.push({
      ymd,
      jourSemaine: parisDayOfWeek(ymd),
      date: new Date(`${ymd}T12:00:00`),
    });
  }
  const lastYmd = days[days.length - 1].ymd;
  const label = new Intl.DateTimeFormat('fr-FR', {
    month: 'long',
    year: 'numeric',
    timeZone: APP_TIMEZONE,
  }).format(new Date(`${firstYmd}T12:00:00`));
  return {
    year,
    month,
    firstYmd,
    lastYmd,
    days,
    label: label.charAt(0).toUpperCase() + label.slice(1),
    dateMin: `${firstYmd}T00:00:00`,
    dateMax: `${lastYmd}T23:59:59`,
  };
}

export function isSameParisDay(isoA, ymdB) {
  if (!isoA || !ymdB) return false;
  const ymdA = new Intl.DateTimeFormat('en-CA', { timeZone: APP_TIMEZONE }).format(new Date(isoA));
  return ymdA === ymdB;
}

export function formatParisDayHeader(date) {
  const label = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: APP_TIMEZONE,
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function formatParisTimeRange(dateDebut, dateFin) {
  const timeFmt = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: APP_TIMEZONE,
  });
  const start = timeFmt.format(new Date(dateDebut));
  const end = timeFmt.format(new Date(dateFin));
  return `${start.replace(':', 'h')} – ${end.replace(':', 'h')}`;
}
